import React, { useEffect, useRef, useState } from 'react';
import { Award, BookOpen, Users, Globe } from 'lucide-react';
import { Card } from './ui/card';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const highlights = [
    {
      id: 'highlight-1',
      icon: Award,
      title: 'Academic Excellence',
      description: 'PhD in English & MPhil in Applied Linguistics from University of Manchester'
    },
    {
      id: 'highlight-2',
      icon: Users,
      title: 'Leadership',
      description: 'Vice President (Academics), Dean, and Department Chairman at IIUI'
    },
    {
      id: 'highlight-3',
      icon: BookOpen,
      title: 'Research Focus',
      description: 'Corpus Linguistics, Discourse Analysis, and Comparative Narrative Studies'
    },
    {
      id: 'highlight-4',
      icon: Globe,
      title: 'Global Impact',
      description: 'International collaborations with UK universities and global research networks'
    }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-3">
            About Me
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto"></div>
        </div>

        {/* Main Content */}
        <div className={`max-w-4xl mx-auto mb-10 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4 text-sm md:text-base">
            <p>
              I am a scholar of English linguistics and literature with a PhD in English and an MPhil in Applied Linguistics from the University of Manchester, UK. My academic journey has also included postdoctoral research at the Universities of Manchester and Birmingham, where I worked on corpus linguistics, narrative discourse, and comparative religious texts. These experiences shaped my interdisciplinary approach to language, literature, and research methodology.
            </p>
            <p>
              Over the course of my career, I have served in several senior academic and administrative roles, including Professor of English, Chairman of the Department of English, Dean of the Faculty of Languages and Literature, Head of the Centre of Excellence for Modern Languages, and Vice President (Academics) at International Islamic University Islamabad. These leadership roles allowed me to contribute to curriculum development, faculty mentoring, quality assurance, and institutional strategy at the highest levels.
            </p>
            <p>
              My passion lies in advancing research-driven teaching, mentoring emerging scholars, and fostering academic environments that encourage critical inquiry, inclusivity, and intellectual rigor. I remain deeply committed to producing impactful research, supervising doctoral work, and contributing to national and international academic communities.
            </p>
          </div>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {highlights.map((item) => {
            const Icon = item.icon;
            return (
              <Card
                key={item.id}
                className={`p-5 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all duration-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <Icon className="text-blue-600" size={24} />
                  </div>
                  <h3 className="font-bold text-base text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;